/**
 * Public API exports for passkey package
 */
export { registerPasskey, authenticatePasskey, isPasskeySupported, } from "./platform.js";
export { PasskeyError, PasskeyRegistrationError, PasskeyAuthenticationError, PasskeyVerificationError, } from "./types.js";
// Export encoding utilities
export { base64UrlToHex, base64ToHex, arrayBufferToBase64Url, base64ToArrayBuffer, arrayBufferToHex, parseDERSignature, parseDERPublicKey, extractPublicKeyFromSpki, } from "./encoding.js";
// Export verification services
export { PasskeyVerificationService, createVerificationService, } from "./verification.js";
// Export formatting utilities
export { encodeRegistrationResponse, encodeAuthenticationResponse, decodeChallenge, parsePublicKey, getPasskeyAddress, createPasskeySigner, } from "./formatting.js";
// Export signature utilities
export { getWebAuthnSignature, normalizeSignature, createSafeWebAuthnSignature, extractClientDataFields, } from "./signature.js";
// Simple challenge manager implementation
export class MemoryChallengeManager {
    challenges = new Map();
    async store(challengeId, challenge) {
        const expiresAt = new Date(Date.now() + 5 * 60 * 1000); // 5 minutes
        this.challenges.set(challengeId, { challenge, expiresAt });
        // Auto-cleanup expired challenges
        this.cleanup();
    }
    async retrieve(challengeId) {
        const data = this.challenges.get(challengeId);
        if (!data)
            return null;
        if (data.expiresAt < new Date()) {
            this.challenges.delete(challengeId);
            return null;
        }
        return data.challenge;
    }
    async remove(challengeId) {
        this.challenges.delete(challengeId);
    }
    async cleanup() {
        const now = new Date();
        for (const [id, data] of this.challenges.entries()) {
            if (data.expiresAt < now) {
                this.challenges.delete(id);
            }
        }
    }
}
// Default challenge manager instance
export const defaultChallengeManager = new MemoryChallengeManager();
// Utility functions for common operations
export async function generateChallengeId() {
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        // Browser environment
        const array = new Uint8Array(16);
        crypto.getRandomValues(array);
        return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
    }
    else {
        // Node.js environment
        const crypto = await import("crypto");
        return crypto.randomBytes(16).toString("hex");
    }
}
/**
 * Helper to detect environment
 */
export function detectEnvironment() {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
        return "client";
    }
    return "server";
}
/**
 * Create a passkey context with auto-detected environment
 */
export function createPasskeyContext(overrides) {
    return {
        environment: detectEnvironment(),
        rpName: "Wafra",
        rpID: typeof process !== "undefined"
            ? process.env.APP_URL || "localhost"
            : "localhost",
        origin: typeof process !== "undefined"
            ? process.env.APP_URL
                ? `https://${process.env.APP_URL}`
                : "http://localhost:8081"
            : window?.location?.origin || "http://localhost:8081",
        ...overrides,
    };
}
